import type {
  Locale,
  OperationalReportContract,
  ReportActivityContract,
  ReportDailyTotalContract,
} from "@chams-pay/contracts";
import { formatDisplayDate, formatWholeFigure, operationsMessages } from "@chams-pay/contracts";
import { periodLabel as paymentPeriodLabel } from "../core/format.js";
import { deploymentIdentity } from "../core/deployment.js";

const REPORT_AMOUNT_LIMIT_MAD = 19_900n;
const REPORT_ROW_LIMIT = 28;

const reportCopy = {
  ar: {
    school: "مؤسسة شمس العلوم",
    schoolLatin: "للتعليم الخصوصي",
    dailyTitle: "التقرير اليومي للمداخيل",
    monthlyTitle: "التقرير الشهري للمداخيل",
    receipts: "وصولات مُصدرة",
    collected: "المجموع المُحصّل",
    pendingCollection: "في انتظار التحصيل",
    collectionDays: "عدد أيام التحصيل",
    period: "التاريخ",
    monthlyPeriod: "الشهر",
    student: "اسم التلميذ",
    kind: "نوع الحركة",
    amount: "المبلغ",
    method: "طريقة الأداء",
    paidMonths: "الأشهر المؤداة",
    date: "تاريخ الأداء",
    dailyTotal: "المجموع",
    total: "الصافي:",
    currency: "درهم",
    empty: "لا توجد حركات مسجلة لهذه الفترة.",
    page: "صفحة",
    of: "من",
    address: "277، شارع ابن سينا، حي تيرت",
    ministryAlt: "وزارة التربية الوطنية",
  },
  fr: {
    school: "Établissement Chams Al-Ouloum",
    schoolLatin: "pour l'enseignement privé",
    dailyTitle: "Rapport journalier des recettes",
    monthlyTitle: "Rapport mensuel des recettes",
    receipts: "REÇUS ÉMIS",
    collected: "TOTAL ENCAISSÉ",
    pendingCollection: "EN ATTENTE D’ENCAISSEMENT",
    collectionDays: "JOURS D’ENCAISSEMENT",
    period: "DATE",
    monthlyPeriod: "MOIS",
    student: "Élève",
    kind: "Mouvement",
    amount: "Montant",
    method: "Mode de paiement",
    paidMonths: "Mois réglés",
    date: "Date",
    dailyTotal: "Total",
    total: "Net :",
    currency: "MAD",
    empty: "Aucun mouvement enregistré pour cette période.",
    page: "Page",
    of: "sur",
    address: "277, avenue Ibn Sina, quartier Tirit",
    ministryAlt: "Ministère de l’Éducation nationale",
  },
} as const;

type ReportPage = {
  readonly activity: readonly ReportActivityContract[];
  readonly dailyTotals: readonly ReportDailyTotalContract[];
  readonly grossMad: bigint;
  readonly cancellationsMad: bigint;
};

export function pageActivity(activity: readonly ReportActivityContract[]): readonly ReportPage[] {
  if (activity.length === 0)
    return [{ activity: [], dailyTotals: [], grossMad: 0n, cancellationsMad: 0n }];

  const pages: ReportPage[] = [];
  let entries: ReportActivityContract[] = [];
  let grossMad = 0n;
  let cancellationsMad = 0n;

  const finishPage = () => {
    if (entries.length === 0) return;
    pages.push({ activity: entries, dailyTotals: [], grossMad, cancellationsMad });
    entries = [];
    grossMad = 0n;
    cancellationsMad = 0n;
  };

  for (const entry of activity) {
    const amountMad = BigInt(entry.amountMad);
    const settlementMad = entry.kind === "settlement" ? amountMad : 0n;
    const exceedsAmount = entries.length > 0 && grossMad + settlementMad > REPORT_AMOUNT_LIMIT_MAD;
    const exceedsRows = entries.length >= REPORT_ROW_LIMIT;

    if (exceedsAmount || exceedsRows) finishPage();

    entries.push(entry);
    if (entry.kind === "settlement") grossMad += amountMad;
    else cancellationsMad += amountMad;
  }

  finishPage();
  return pages;
}

export function pageDailyTotals(
  dailyTotals: readonly ReportDailyTotalContract[],
  activity: readonly ReportActivityContract[] = [],
): readonly ReportPage[] {
  if (dailyTotals.length === 0)
    return [{ activity: [], dailyTotals: [], grossMad: 0n, cancellationsMad: 0n }];

  const pages: ReportPage[] = [];
  for (let index = 0; index < dailyTotals.length; index += REPORT_ROW_LIMIT) {
    const entries = dailyTotals.slice(index, index + REPORT_ROW_LIMIT);
    const dates = new Set(entries.map((entry) => entry.date));
    const pageActivity = activity.filter((entry) => dates.has(entry.date));
    let grossMad = 0n;
    let cancellationsMad = 0n;
    for (const entry of pageActivity) {
      if (entry.kind === "settlement") grossMad += BigInt(entry.amountMad);
      else cancellationsMad += BigInt(entry.amountMad);
    }
    pages.push({ activity: pageActivity, dailyTotals: entries, grossMad, cancellationsMad });
  }
  return pages;
}

function periodLabel(report: OperationalReportContract, locale: Locale): string {
  if (report.mode === "daily") return formatDisplayDate(report.period.from);
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${report.period.from}T12:00:00Z`));
}

function ContactIcon({ kind }: { readonly kind: "home" | "mail" | "phone" }) {
  if (kind === "home")
    return (
      <svg className="operational-report__icon" viewBox="0 0 24 24" fill="none">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
        <path d="M9 22V12h6v10" />
      </svg>
    );
  if (kind === "mail")
    return (
      <svg className="operational-report__icon" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 6-10 7L2 6" />
      </svg>
    );
  return (
    <svg className="operational-report__icon" viewBox="0 0 24 24" fill="none">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.95.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.86.58 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function Figure({ value, signed = false }: { readonly value: bigint; readonly signed?: boolean }) {
  return (
    <bdi dir="ltr" className="operational-report__number">
      {formatWholeFigure(signed ? -value : value)}
    </bdi>
  );
}

function countedLabel(value: number, kind: "cheque" | "transfer" | "day", locale: Locale): string {
  if (locale === "fr") {
    const label =
      kind === "cheque"
        ? value === 1
          ? "chèque"
          : "chèques"
        : kind === "transfer"
          ? value === 1
            ? "virement"
            : "virements"
          : value === 1
            ? "jour"
            : "jours";
    return `${formatWholeFigure(BigInt(value))} ${label}`;
  }

  const category = new Intl.PluralRules("ar-MA").select(value);
  const forms = {
    cheque: {
      zero: "شيك",
      one: "شيك واحد",
      two: "شيكان",
      few: "شيكات",
      many: "شيكًا",
      other: "شيك",
    },
    transfer: {
      zero: "تحويل",
      one: "تحويل واحد",
      two: "تحويلان",
      few: "تحويلات",
      many: "تحويلًا",
      other: "تحويل",
    },
    day: {
      zero: "يوم",
      one: "يوم واحد",
      two: "يومان",
      few: "أيام",
      many: "يومًا",
      other: "يوم",
    },
  } as const;
  const label = forms[kind][category];
  if (category === "one" || category === "two") return label;
  return `${formatWholeFigure(BigInt(value))} ${label}`;
}

/**
 * A report is a sequence of fixed A4-safe sheets. Daily transaction pages
 * retain the legacy 19,900 MAD ceiling; monthly summary pages use only the
 * 28-row layout ceiling.
 */
export function ReportDocument({
  fitA4Preview = false,
  locale,
  report,
}: {
  readonly fitA4Preview?: boolean;
  readonly locale: Locale;
  readonly report: OperationalReportContract;
}) {
  const text = operationsMessages(locale);
  const copy = reportCopy[locale];
  const schoolName =
    locale === "ar" ? deploymentIdentity.schoolNameAr : deploymentIdentity.schoolNameFr;
  const schoolAddress =
    locale === "ar" ? deploymentIdentity.addressAr : deploymentIdentity.addressFr;
  const displayPeriod = periodLabel(report, locale);
  const monthly = report.mode === "monthly";
  const pages = monthly
    ? pageDailyTotals(report.dailyTotals, report.activity)
    : pageActivity(report.activity);

  return (
    <section
      className="operational-report"
      lang={locale}
      dir={locale === "ar" ? "rtl" : "ltr"}
      data-a4-preview={fitA4Preview || undefined}
      data-testid="operational-report"
    >
      {pages.map((page, pageIndex) => {
        const settlements = page.activity.filter((entry) => entry.kind === "settlement").length;
        const netMad = page.grossMad - page.cancellationsMad;
        const dense = (monthly ? page.dailyTotals.length : page.activity.length) >= 15;
        const key = `${pageIndex}-${
          monthly
            ? (page.dailyTotals[0]?.date ?? "empty")
            : (page.activity[0]?.reference ?? "empty")
        }`;

        const reportPage = (
          <article className="operational-report__page" data-dense={dense || undefined} key={key}>
            <header className="operational-report__header">
              <div className="operational-report__logos">
                <div className="operational-report__brand">
                  <img
                    className="operational-report__chams-mark"
                    src="/brand-logo.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    <strong>{schoolName}</strong>
                    <small>{copy.schoolLatin}</small>
                  </span>
                </div>
                <img
                  className="operational-report__ministry"
                  src={locale === "ar" ? "/logo-ministere-ar.svg" : "/logo-ministere-fr.svg"}
                  alt={copy.ministryAlt}
                />
              </div>
              <hr />
            </header>

            <div className="operational-report__content">
              <div className="operational-report__title">
                <h2>{monthly ? copy.monthlyTitle : copy.dailyTitle}</h2>
              </div>

              <div className="operational-report__date">
                <span>
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                  <bdi dir="ltr">{displayPeriod}</bdi>
                </span>
                {pages.length > 1 && (
                  <small className="operational-report__page-number">
                    {copy.page} <bdi dir="ltr">{pageIndex + 1}</bdi> {copy.of}{" "}
                    <bdi dir="ltr">{pages.length}</bdi>
                  </small>
                )}
              </div>

              <div className="operational-report__stats">
                <div>
                  <span>{copy.receipts}</span>
                  <strong>
                    <Figure value={BigInt(settlements)} />
                  </strong>
                </div>
                <div>
                  <span>{copy.collected}</span>
                  <strong className="operational-report__accent">
                    <Figure value={netMad} />
                  </strong>
                </div>
                <div>
                  <span>{monthly ? copy.collectionDays : copy.pendingCollection}</span>
                  <strong className="operational-report__period">
                    {monthly ? (
                      <bdi dir="auto">{countedLabel(report.dailyTotals.length, "day", locale)}</bdi>
                    ) : (
                      <>
                        <bdi dir="auto">
                          {countedLabel(report.pendingInstruments.chequeCount, "cheque", locale)}
                        </bdi>{" "}
                        ·{" "}
                        <bdi dir="auto">
                          {countedLabel(
                            report.pendingInstruments.transferCount,
                            "transfer",
                            locale,
                          )}
                        </bdi>
                      </>
                    )}
                  </strong>
                </div>
              </div>

              <table className="operational-report__table">
                {monthly ? (
                  <colgroup>
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "28%" }} />
                  </colgroup>
                )}
                <thead>
                  {monthly ? (
                    <tr>
                      <th>{copy.date}</th>
                      <th>{text.cash}</th>
                      <th>{text.cheque}</th>
                      <th>{text.transfer}</th>
                      <th className="operational-report__daily-total">{copy.dailyTotal}</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>{copy.student}</th>
                      <th>{copy.kind}</th>
                      <th>{copy.amount}</th>
                      <th>{copy.method}</th>
                      <th>{copy.paidMonths}</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {monthly ? (
                    page.dailyTotals.length ? (
                      page.dailyTotals.map((entry) => (
                        <tr key={entry.date}>
                          <td className="operational-report__number operational-report__date-cell">
                            <bdi dir="ltr">{formatDisplayDate(entry.date)}</bdi>
                          </td>
                          {(["cashMad", "chequeMad", "transferMad", "totalMad"] as const).map(
                            (key) => (
                              <td
                                className={`operational-report__amount${
                                  key === "totalMad" ? " operational-report__daily-total" : ""
                                }`}
                                key={key}
                              >
                                <Figure value={BigInt(entry[key])} />
                              </td>
                            ),
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr className="operational-report__empty">
                        <td colSpan={5}>{copy.empty}</td>
                      </tr>
                    )
                  ) : page.activity.length ? (
                    page.activity.map((entry, index) => {
                      const amountMad = BigInt(entry.amountMad);
                      const cancelled = entry.kind === "cancellation";
                      const studentName =
                        locale === "ar" ? entry.studentNameAr : entry.studentNameFr;
                      const paidMonths = entry.paidPeriods
                        .map((period) => paymentPeriodLabel(period, locale))
                        .join(locale === "ar" ? "، " : " · ");
                      return (
                        <tr key={`${entry.kind}-${entry.reference}-${index}`}>
                          <td title={studentName}>{studentName}</td>
                          <td>{text[entry.kind]}</td>
                          <td
                            className="operational-report__amount"
                            data-cancelled={cancelled || undefined}
                          >
                            <Figure value={amountMad} signed={cancelled} />{" "}
                            <small>{copy.currency}</small>
                          </td>
                          <td>{text[entry.method]}</td>
                          <td title={paidMonths}>{paidMonths || "—"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="operational-report__empty">
                      <td colSpan={5}>{copy.empty}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="operational-report__total">
                <strong>{copy.total}</strong>
                <span>
                  <Figure value={netMad} /> <small>{copy.currency}</small>
                </span>
              </div>
            </div>

            <footer className="operational-report__footer">
              <hr />
              <div>
                <span>
                  <ContactIcon kind="home" />
                  {schoolAddress}
                </span>
                <span>
                  <ContactIcon kind="mail" />
                  <bdi dir="ltr">{deploymentIdentity.email}</bdi>
                </span>
                <span>
                  <ContactIcon kind="phone" />
                  <bdi dir="ltr">{deploymentIdentity.phone}</bdi>
                </span>
              </div>
            </footer>
          </article>
        );

        return fitA4Preview ? (
          <div className="operational-report__sheet" key={key}>
            {reportPage}
          </div>
        ) : (
          reportPage
        );
      })}
    </section>
  );
}
